import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/context/i18n";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Check, X, Landmark } from "lucide-react";

const STATUS_STYLE = {
  PENDING: "bg-amber-100 text-amber-700",
  CONFIRMED: "bg-green-100 text-green-700",
  CANCELLED: "bg-red-100 text-red-600",
};

export default function Admin() {
  const { user, authLoading, formatPrice } = useApp();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [busy, setBusy] = useState("");

  const load = () => api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {}).finally(() => setLoading(false));

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      sessionStorage.setItem("postLoginRedirect", "/admin");
      const redirectUrl = window.location.origin + "/";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    if (!user.isAdmin) { setLoading(false); return; }
    load();
    // eslint-disable-next-line
  }, [user, authLoading]);

  if (!authLoading && user && !user.isAdmin) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]"><Header />
        <div className="pt-40 text-center" data-testid="admin-denied">
          <ShieldCheck className="w-12 h-12 text-[#475569] mx-auto mb-3" />
          <p className="text-[#0B2038] font-semibold">{t("access_denied")}</p>
          <Button onClick={() => navigate("/")} className="mt-4 rounded-full bg-[#0B2038] text-white">{t("back_home")}</Button>
        </div>
      </div>
    );
  }

  const act = async (ref, action) => {
    setBusy(ref);
    try {
      await api.post(`/admin/bookings/${ref}/${action}`);
      toast.success(action === "confirm" ? t("mark_paid_done") : t("reject_booking"));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally { setBusy(""); }
  };

  const wirePending = bookings.filter((b) => b.paymentMethod === "wire" && b.status === "PENDING");
  const shown = filter === "pending" ? wirePending : bookings;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-page">
      <Header />
      <div className="pt-24 max-w-6xl mx-auto px-4 lg:px-8 min-h-[60vh]">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <span className="label-caps flex items-center gap-2"><Landmark className="w-4 h-4 text-[#009E9E]" />{t("admin")}</span>
            <h1 className="font-display text-3xl lg:text-4xl font-bold text-[#0B2038] mt-1">{t("admin_title")}</h1>
            <p className="text-[#475569] mt-1">{t("admin_sub")}</p>
          </div>
          <div className="flex gap-2">
            {[["pending", `${t("filter_pending")} (${wirePending.length})`], ["all", t("filter_all")]].map(([f, label]) => (
              <button key={f} data-testid={`admin-filter-${f}`} onClick={() => setFilter(f)}
                className={`h-10 px-4 rounded-full text-sm font-bold border transition-colors ${filter === f ? "bg-[#0B2038] text-white border-[#0B2038]" : "border-[#E2E8F0] text-[#475569] hover:border-[#0B2038]"}`}>{label}</button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#009E9E]" /></div>
        ) : shown.length === 0 ? (
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-12 text-center text-[#475569]" data-testid="admin-empty">{t("no_admin_bookings")}</div>
        ) : (
          <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-table">
                <thead className="bg-[#F8FAFC] text-[#475569] text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left font-bold px-4 py-3">{t("ref")}</th>
                    <th className="text-left font-bold px-4 py-3">{t("guest")}</th>
                    <th className="text-left font-bold px-4 py-3 hidden md:table-cell">Hotel</th>
                    <th className="text-left font-bold px-4 py-3">{t("method")}</th>
                    <th className="text-right font-bold px-4 py-3">{t("amount")}</th>
                    <th className="text-left font-bold px-4 py-3">{t("booking_status")}</th>
                    <th className="text-right font-bold px-4 py-3">{t("actions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]">
                  {shown.map((b) => (
                    <tr key={b.reference} data-testid={`admin-row-${b.reference}`} className="hover:bg-[#F8FAFC]">
                      <td className="px-4 py-3 font-mono font-bold text-[#0B2038]">{b.reference}</td>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-[#0B2038]">{b.holder?.name} {b.holder?.surname}</p>
                        <p className="text-xs text-[#475569]">{b.holder?.email}</p>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-[#475569] max-w-[220px] truncate">{b.title}</td>
                      <td className="px-4 py-3">{b.paymentMethod === "wire" ? t("pay_wire") : t("pay_card")}</td>
                      <td className="px-4 py-3 text-right font-display font-bold text-[#0B2038]">{formatPrice(b.priceEur)}</td>
                      <td className="px-4 py-3"><span className={`text-xs font-bold px-2.5 py-1 rounded-full ${STATUS_STYLE[b.status] || "bg-slate-100 text-slate-600"}`}>{b.status}</span></td>
                      <td className="px-4 py-3">
                        {b.paymentMethod === "wire" && b.status === "PENDING" ? (
                          <div className="flex items-center justify-end gap-2">
                            <button data-testid={`admin-confirm-${b.reference}`} disabled={busy === b.reference} onClick={() => act(b.reference, "confirm")}
                              className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-[#009E9E] hover:bg-[#007A7A] text-white text-xs font-bold disabled:opacity-50">
                              {busy === b.reference ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}{t("confirm_payment")}
                            </button>
                            <button data-testid={`admin-reject-${b.reference}`} disabled={busy === b.reference} onClick={() => act(b.reference, "reject")}
                              className="inline-flex items-center gap-1 h-9 px-3 rounded-lg border border-[#E2E8F0] text-[#475569] hover:border-red-400 hover:text-red-500 text-xs font-bold">
                              <X className="w-3.5 h-3.5" />{t("reject_booking")}
                            </button>
                          </div>
                        ) : <span className="text-xs text-[#94A3B8] block text-right">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
